import { useState, ImgHTMLAttributes } from "react"

export type ImageWithFallbackProps = ImgHTMLAttributes<HTMLImageElement> & {
  fallbackSrc?: string
  src?: string
  alt: string
  width?: number | string
  height?: number | string
}

/**
 * Image component with automatic fallback support
 *
 * Displays a fallback image when the primary image fails to load.
 * Useful for merchant logos and dynamic images from external sources.
 *
 * @param fallbackSrc - Path to fallback image (defaults to "/pay2B_g.png")
 * @param src - Primary image source
 * @param alt - Alternative text for accessibility
 * @param props - All other img props
 *
 * @example
 * ```tsx
 * <ImageWithFallback
 *   src={merchant?.logo}
 *   fallbackSrc="/logo-portal-seguros.svg"
 *   alt="Merchant Logo"
 *   width={80}
 *   height={80}
 * />
 * ```
 */
export const ImageWithFallback = ({
  fallbackSrc = "/pay2B_g.png",
  src,
  alt,
  ...props
}: ImageWithFallbackProps) => {
  const [imgSrc, setImgSrc] = useState(src)
  const [hasError, setHasError] = useState(false)

  const handleError = () => {
    if (!hasError && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc)
      setHasError(true)
    }
  }

  return (
    <img
      {...props}
      src={imgSrc || fallbackSrc}
      alt={alt}
      onError={handleError}
    />
  )
}
